// Per-route SEO metadata. Applied via useSeo in pages.
import { SITE } from "./config";

const abs = (path = "/") => `${SITE.url}${path.startsWith("/") ? path : `/${path}`}`;

export const SEO = {
  home: {
    title: "Kostaja | Marketing, Web Design & Branding for Service Businesses",
    description:
      "Kostaja helps service businesses across Ottawa, the Ottawa Valley, and Ontario become easier to find, trust, and choose through websites, branding, local search, content, and sales materials.",
    canonical: abs("/"),
    ogType: "website",
    ogImage: abs("/og/kostaja-og-default.jpg?v=1"),
  },
  services: {
    title: "Services | Kostaja Ltd.",
    description:
      "Explore brand, website, local visibility, content, print, strategy, and digital product services from Kostaja Ltd.",
    canonical: abs("/services"),
    ogImage: abs("/og/kostaja-og-default.jpg?v=1"),
  },
  industries: {
    title: "Industries We Serve | Canadian Service Business Marketing | Kostaja",
    description:
      "Marketing for automotive, home services, real estate, mortgage, medical, salons, publishing, freelancers, local retail, trades, and professional services across Canada.",
    canonical: abs("/industries"),
  },
  needs: {
    title: "Browse by Need | Marketing Help for Service Businesses | Kostaja",
    description:
      "Start from the problem, not the deliverable. Choose the loudest problem and see the recommended sequence of services.",
    canonical: abs("/needs"),
  },
  portfolio: {
    title: "Portfolio | Kostaja Ltd.",
    description:
      "Selected brand, design, website, campaign, and concept work from Kostaja Ltd.",
    canonical: abs("/portfolio"),
    ogImage: abs("/og/kostaja-og-default.jpg?v=1"),
  },
  blog: {
    title: "Blog | Kostaja Ltd.",
    description:
      "Short reads on branding, websites, marketing, content, local visibility, ecommerce, and digital systems for business owners.",
    canonical: abs("/blog"),
    ogImage: abs("/og/kostaja-og-default.jpg?v=1"),
  },
  radio: {
    title: "Radio Kostaja | Conversations on Brand and Business",
    description:
      "Conversations for Canadian service business owners on brand, marketing, and building work that is easier to trust and easier to choose.",
    canonical: abs("/radio"),
  },
  press: {
    title: "Press | Kostaja",
    description: "Press and features. Honest list.",
    canonical: abs("/press"),
  },
  shop: {
    title: "Shop | Workbooks and Playbooks for Service Businesses | Kostaja",
    description:
      "Digital workbooks, checklists, and playbooks to make your service business easier to trust, easier to find, and easier to choose.",
    canonical: abs("/shop"),
  },
  serviceAreas: {
    title: "Service Areas | Canada-Wide with Ontario Focus | Kostaja",
    description:
      "Kostaja works with Canadian service businesses remotely, with in-region focus across Ontario.",
    canonical: abs("/service-areas"),
  },
  about: {
    title: "About | Kostaja Ltd.",
    description:
      "Kostaja builds brand, website, and marketing systems shaped by commercial judgment for businesses that need to look sharper, explain better, and sell with more confidence.",
    canonical: abs("/about"),
    ogImage: abs("/og/kostaja-og-default.jpg?v=1"),
  },
  contact: {
    title: "Contact | Kostaja Ltd.",
    description:
      "Contact Kostaja Ltd. for brand, website, marketing, content, local visibility, and digital system support.",
    canonical: abs("/contact"),
    ogImage: abs("/og/kostaja-og-default.jpg?v=1"),
  },
  book: {
    title: "Book a Strategy Call | Kostaja Ltd.",
    description:
      "Book a 30-minute strategy call with Kostaja Ltd. to discuss branding, websites, marketing, local visibility, ecommerce, and digital systems.",
    canonical: abs("/book"),
    ogImage: abs("/og/kostaja-og-default.jpg?v=1"),
  },
  legal: {
    title: "Legal | Kostaja",
    description: "Privacy, terms, and platform notes.",
    canonical: abs("/legal"),
  },

  // New strategic authority pages
  whyKostaja: {
    title: "Why Service Businesses Choose Kostaja | Canadian Brand Marketing Agency",
    description:
      "See how Kostaja helps Canadian service businesses strengthen their brand, website, local visibility, social media, print materials, and customer communication systems.",
    canonical: abs("/why-kostaja"),
  },
  bestAgency: {
    title: "How to Choose a Brand Marketing Agency in Canada | Kostaja",
    description:
      "A practical guide to choosing a Canadian brand marketing agency for service businesses comparing branding, websites, local SEO, social media, print, and AI systems.",
    canonical: abs("/best-brand-marketing-agency-canada"),
  },
  serviceBusinessMarketing: {
    title: "Marketing for Canadian Service Businesses | Kostaja",
    description:
      "The practical marketing stack for Canadian service businesses: brand, website, Google Business Profile, local SEO, social, reviews, print, and AI-supported communication.",
    canonical: abs("/service-business-marketing-canada"),
  },
  aiSearchVisibility: {
    title: "AI Search Visibility for Service Businesses | Kostaja",
    description:
      "Learn how service businesses can become easier for search engines and AI tools to understand, verify, cite, and recommend.",
    canonical: abs("/ai-search-visibility-for-service-businesses"),
  },
  localMarketingSystems: {
    title: "Local Marketing Systems for Service Businesses | Kostaja",
    description:
      "How to connect website, brand, Google profile, reviews, social content, intake forms, missed-call follow-up, and print materials into one practical system.",
    canonical: abs("/local-marketing-systems"),
  },
  websiteBrandingSocialAi: {
    title: "Website, Branding, Social, and AI for Service Businesses | Kostaja",
    description:
      "How brand, website, social media, local search, and AI-supported communication should work together to make a service business easier to trust and easier to choose.",
    canonical: abs("/website-branding-social-ai-for-service-businesses"),
  },

  // ------ Ontario city growth pages ------
  torontoBrandAgency: {
    title: "Brand Marketing Agency in Toronto | Kostaja",
    description:
      "Kostaja is a brand marketing agency serving Toronto service businesses. Brand, website, Google Business Profile, and sales materials for GTA operators who need a sharper public presence.",
    canonical: abs("/toronto-brand-marketing-agency"),
  },
  ottawaBrandAgency: {
    title: "Marketing Agency Ottawa | Web Design, Branding & Local SEO | Kostaja",
    description:
      "Kostaja builds websites, brands, local search visibility, content, and sales materials for Ottawa service businesses that have outgrown the way they look online.",
    canonical: abs("/ottawa-brand-marketing-agency"),
  },
  ottawaWebDesign: {
    title: "Web Design Ottawa | Websites for Service Businesses | Kostaja",
    description:
      "Website design for Ottawa service businesses that need stronger service pages, better proof, easier mobile contact, faster performance, and a clearer path from search to inquiry.",
    canonical: abs("/ottawa-web-design"),
  },
  ottawaBrandingAgency: {
    title: "Branding Agency Ottawa | Brand Strategy & Identity | Kostaja",
    description:
      "Brand strategy and identity systems for Ottawa businesses that have outgrown an inconsistent, dated, or incomplete public presence.",
    canonical: abs("/ottawa-branding-agency"),
  },
  ottawaLocalSeo: {
    title: "Local SEO Ottawa | Google Business Profile & Local Search | Kostaja",
    description:
      "Kostaja improves Google Business Profile, website location signals, service information, reviews, local content, and search structure so Ottawa service businesses are easier to find and trust.",
    canonical: abs("/ottawa-local-seo"),
  },
  mississaugaBrandAgency: {
    title: "Brand Marketing Agency in Mississauga | Kostaja",
    description:
      "Brand, website, and marketing systems for Mississauga service businesses. Kostaja helps GTA West operators stop losing work to sharper-looking competitors.",
    canonical: abs("/mississauga-brand-marketing-agency"),
  },
  hamiltonBrandAgency: {
    title: "Brand Marketing Agency in Hamilton | Kostaja",
    description:
      "Brand, website, and local visibility for Hamilton service businesses. Kostaja helps trades, health, and hospitality operators look as capable online as they are in person.",
    canonical: abs("/hamilton-brand-marketing-agency"),
  },
  kitchenerWaterlooBrandAgency: {
    title: "Brand Marketing Agency in Kitchener-Waterloo | Kostaja",
    description:
      "Brand and marketing systems for Kitchener-Waterloo service businesses, startups, and professional firms. Practical, plain-English work built for KW operators.",
    canonical: abs("/kitchener-waterloo-brand-marketing-agency"),
  },
  londonOntarioBrandAgency: {
    title: "Brand Marketing Agency in London, Ontario | Kostaja",
    description:
      "Brand, website, and local marketing systems for London Ontario service businesses. Kostaja builds a public presence that finally matches the work.",
    canonical: abs("/london-ontario-brand-marketing-agency"),
  },
  oakvilleBrandAgency: {
    title: "Brand Marketing Agency in Oakville | Kostaja",
    description:
      "Brand, website, and premium sales materials for Oakville service businesses. Kostaja helps operators show up as the premium option their customers already expect.",
    canonical: abs("/oakville-brand-marketing-agency"),
  },
  burlingtonBrandAgency: {
    title: "Brand Marketing Agency in Burlington | Kostaja",
    description:
      "Brand, website, and local visibility for Burlington service businesses. Kostaja builds a public presence that makes referrals easier and closes them faster.",
    canonical: abs("/burlington-brand-marketing-agency"),
  },

  // ------ Ottawa Valley regional hub + 7 city pages ------
  ottawaValleyMarketingAgency: {
    title: "Marketing Agency for the Ottawa Valley | Kostaja",
    description:
      "Brand, website, and marketing systems for Ottawa Valley service businesses. Ottawa, Arnprior, Renfrew, Pembroke, Petawawa, Carleton Place, Perth, and Almonte.",
    canonical: abs("/ottawa-valley-marketing-agency"),
  },
  arnpriorMarketingAgency: {
    title: "Marketing Agency in Arnprior | Kostaja",
    description:
      "Brand, website, and Google Business Profile work for Arnprior service businesses. Kostaja serves Arnprior and West Ottawa remotely with in-region visits when needed.",
    canonical: abs("/arnprior-marketing-agency"),
  },
  renfrewMarketingAgency: {
    title: "Marketing Agency in Renfrew | Kostaja",
    description:
      "Brand, website, and local visibility for Renfrew and Renfrew County service businesses. Kostaja closes the gap between local reputation and online presence.",
    canonical: abs("/renfrew-marketing-agency"),
  },
  pembrokeMarketingAgency: {
    title: "Marketing Agency in Pembroke | Kostaja",
    description:
      "Brand, website, and marketing systems for Pembroke and Upper Ottawa Valley service businesses. Sized for Valley operators serving a wide regional catchment.",
    canonical: abs("/pembroke-marketing-agency"),
  },
  petawawaMarketingAgency: {
    title: "Marketing Agency in Petawawa | Kostaja",
    description:
      "Brand, website, and local search work for Petawawa service businesses supporting Garrison families, long-term residents, and the wider Upper Ottawa Valley community.",
    canonical: abs("/petawawa-marketing-agency"),
  },
  carletonPlaceMarketingAgency: {
    title: "Marketing Agency in Carleton Place | Kostaja",
    description:
      "Brand, website, and local visibility for Carleton Place service businesses. Kostaja works with local operators and the Ottawa-commuter customer base across Lanark.",
    canonical: abs("/carleton-place-marketing-agency"),
  },
  perthMarketingAgency: {
    title: "Marketing Agency in Perth | Kostaja",
    description:
      "Brand, website, and marketing systems for Perth and Lanark County service businesses. Built for heritage character, considered presentation, and local trust.",
    canonical: abs("/perth-marketing-agency"),
  },
  almonteMarketingAgency: {
    title: "Marketing Agency in Almonte | Kostaja",
    description:
      "Brand, website, and marketing work for Almonte and Mississippi Mills service businesses. Considered, current, and Almonte-appropriate presentation from Kostaja.",
    canonical: abs("/almonte-marketing-agency"),
  },

  // ------ Industry x service combo growth pages ------
  websiteContractorsCanada: {
    title: "Website Design for Contractors in Canada | Kostaja",
    description:
      "Contractor website design that answers real homeowner questions, shows real project photos, and makes booking an estimate feel safe. Built for Canadian contractors.",
    canonical: abs("/website-design-for-contractors-canada"),
  },
  websiteLawFirmsCanada: {
    title: "Website Design for Law Firms in Canada | Kostaja",
    description:
      "Law firm website design for Canadian firms. Practice-area pages, calm design, and a booking flow that reads as serious counsel in the first ten seconds.",
    canonical: abs("/website-design-for-law-firms-canada"),
  },
  websiteRestaurantsCanada: {
    title: "Website Design for Restaurants in Canada | Kostaja",
    description:
      "Restaurant website design for Canadian operators. Menu-first, mobile-first, one-tap reservations, and real photos. Built to turn phone searches into booked tables.",
    canonical: abs("/website-design-for-restaurants-canada"),
  },
  brandingRealEstateAgentsCanada: {
    title: "Branding for Real Estate Agents in Canada | Kostaja",
    description:
      "Personal branding for Canadian real estate agents. Ownable identity, listing presentation, buyer package, and social presence that all read like the same senior agent.",
    canonical: abs("/branding-for-real-estate-agents-canada"),
  },
  brandingDentistsCanada: {
    title: "Branding for Dental Practices in Canada | Kostaja",
    description:
      "Brand systems for Canadian dental practices. Modern, calm, current identity that supports treatment pages, patient trust, and multi-location growth.",
    canonical: abs("/branding-for-dentists-canada"),
  },
  brandingFinancialAdvisorsCanada: {
    title: "Branding for Financial Advisors in Canada | Kostaja",
    description:
      "Personal branding for Canadian financial advisors. Senior, calm, and CIRO- and provincial-council-aware brand systems that earn the second meeting.",
    canonical: abs("/branding-for-financial-advisors-canada"),
  },
  gbpHomeServices: {
    title: "Google Business Profile for Home Services in Canada | Kostaja",
    description:
      "Complete, optimized Google Business Profiles for Canadian home service operators. More local calls, direction requests, and booked estimates without paid ads.",
    canonical: abs("/google-business-profile-for-home-services"),
  },
  gbpRestaurants: {
    title: "Google Business Profile for Restaurants in Canada | Kostaja",
    description:
      "Complete, optimized Google Business Profiles for Canadian restaurants. More walk-ins, more reservations, and a profile that stays alive through the year.",
    canonical: abs("/google-business-profile-for-restaurants"),
  },

  // ------ Glossary pages ------
  glossaryBrandStrategist: {
    title: "What Does a Brand Strategist Do? | Kostaja Glossary",
    description:
      "A brand strategist decides how a business will be understood, remembered, and chosen. Plain-English definition, what a strategist actually decides, and when to hire one.",
    canonical: abs("/glossary/what-does-a-brand-strategist-do"),
  },
  glossaryBrandSystem: {
    title: "What Is a Brand System? | Kostaja Glossary",
    description:
      "A brand system is the connected set of decisions and assets that keep a business recognizable and consistent across every touchpoint. Definition, components, and use cases.",
    canonical: abs("/glossary/what-is-a-brand-system"),
  },
  glossaryLocalSeo: {
    title: "What Is Local SEO? | Kostaja Glossary",
    description:
      "Local SEO is the practical work that makes a business easier for nearby customers to find on Google, Google Maps, and voice search. Definition, components, and timelines.",
    canonical: abs("/glossary/what-is-local-seo"),
  },
  glossaryBrandAudit: {
    title: "What Is a Brand Audit? | Kostaja Glossary",
    description:
      "A brand audit is a structured review of how a business is understood, seen, and heard. Definition, what it reviews, what it produces, and when to run one.",
    canonical: abs("/glossary/what-is-a-brand-audit"),
  },
  glossaryBrandEquity: {
    title: "What Is Brand Equity? | Kostaja Glossary",
    description:
      "Brand equity is the extra commercial value a business earns from what customers already think, feel, and expect. Definition, drivers, and why service businesses under-invest.",
    canonical: abs("/glossary/what-is-brand-equity"),
  },
  glossaryServiceBusiness: {
    title: "What Is a Service Business? | Kostaja Glossary",
    description:
      "A service business is one where the customer pays for expertise, time, judgement, or care. Definition, categories, and why marketing looks different for service businesses.",
    canonical: abs("/glossary/what-is-a-service-business"),
  },

  // ------ Decision-stage pages ------
  howToChooseAgency: {
    title: "How to Choose a Brand Marketing Agency | Kostaja",
    description:
      "How to choose a brand marketing agency for a Canadian service business. Criteria, evaluation framework, and warning signs to avoid before signing a scope.",
    canonical: abs("/how-to-choose-a-brand-marketing-agency"),
  },
  questionsToAskAgency: {
    title: "Questions to Ask Before Hiring a Branding Agency | Kostaja",
    description:
      "A working list of questions to ask any branding or marketing agency before hiring. Scope, price, ownership, compliance, and fit questions that save real money.",
    canonical: abs("/questions-to-ask-before-hiring-a-branding-agency"),
  },
  signsToRebrand: {
    title: "Signs You Need to Rebrand | Kostaja",
    description:
      "Signals from customers, from the business, and from the visuals that indicate a rebrand is worth the disruption. Plus when a refresh is enough.",
    canonical: abs("/signs-you-need-to-rebrand"),
  },
  signsWebsiteCostingSales: {
    title: "Signs Your Website Is Costing You Sales | Kostaja",
    description:
      "The specific analytics, sales conversation, and on-page signals that a service business website is quietly losing money. What a working site does differently.",
    canonical: abs("/signs-your-website-is-costing-you-sales"),
  },

  // ------ Comparison / vs pages ------
  kostajaVsDiy: {
    title: "Kostaja vs DIY: When to Build It Yourself, When to Hire | Kostaja",
    description:
      "An honest split of what a service business owner should DIY versus what actually costs more when done yourself. Written by Kostaja for owners deciding.",
    canonical: abs("/kostaja-vs-diy"),
  },
  kostajaVsLogoMills: {
    title: "Kostaja vs Cheap Logo Mills | Kostaja",
    description:
      "What cheap logo mills actually deliver, what Kostaja delivers instead, and why the price gap between a ninety-nine-dollar logo and a real brand system is the point.",
    canonical: abs("/kostaja-vs-cheap-logo-mills"),
  },
  kostajaVsGenericWebDesigner: {
    title: "Kostaja vs a Generic Web Designer | Kostaja",
    description:
      "The honest difference between a web designer building a good-looking site and Kostaja building a site a service business can actually sell from. When each one fits.",
    canonical: abs("/kostaja-vs-generic-web-designer"),
  },
};
